// Modal grid of all 10 card designs, rendered as thumbnails with the user's
// actual data. Clicking a thumb selects it; the parent persists via PUT /me.
import { useEffect } from "react";
import { BusinessCard, CARD_STYLES, type CardStyle, type CardUser } from "./BusinessCard";

interface Props {
  open: boolean;
  current: CardStyle;
  user: CardUser;
  onSelect: (style: CardStyle) => void;
  onClose: () => void;
}

export function CardStylePicker({ open, current, user, onSelect, onClose }: Props) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cardpicker-title"
      onClick={onClose}
    >
      <div
        className="relative max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-lg border border-rule bg-paper p-7 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-7 flex items-baseline justify-between gap-6">
          <div>
            <p className="eyebrow eyebrow-accent">Card design</p>
            <h2 id="cardpicker-title" className="mt-2 text-4xl tracking-tight">
              Pick a card style.
            </h2>
            <p className="mt-2 text-sm text-ink-mute">
              Ten hand-built designs. Click any card to apply — your contact details
              get poured into the chosen layout.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-rule px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-mute hover:text-ink"
            aria-label="Close picker"
          >
            Close ×
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {CARD_STYLES.map((s) => {
            const isCurrent = s.value === current;
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => onSelect(s.value)}
                className={`group relative flex flex-col gap-3 rounded-md border p-4 text-left transition ${
                  isCurrent
                    ? "border-terra bg-paper-deep ring-2 ring-terra/40"
                    : "border-rule bg-cream-card hover:border-ink hover:-translate-y-0.5"
                }`}
              >
                {isCurrent && (
                  <span className="absolute right-3 top-3 z-10 rounded-full bg-terra px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-paper">
                    Current
                  </span>
                )}
                <div className="grid h-44 place-items-center overflow-hidden rounded bg-paper">
                  <BusinessCard variant={s.value} user={user} size="thumb" />
                </div>
                <div>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-display text-lg tracking-tight">{s.label}</span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-ink-mute">
                      {s.tag}
                    </span>
                  </div>
                  <p className="mt-1 text-xs leading-snug text-ink-mute">{s.blurb}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
